import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { GarmentCreate } from 'src/app/models/garment';
import { GarmentService } from 'src/app/servises/garment.service';
import { ImageService } from 'src/app/servises/image.service';
import { GenderService } from 'src/app/servises/gender.service';
import { CategoryService } from 'src/app/servises/category.service';
import { GarmentTypeService } from 'src/app/servises/garment-type.service';
import { ColorService } from 'src/app/servises/color.service';
import { SeasonService } from 'src/app/servises/season.service';
import { UsageService } from 'src/app/servises/usage.service';
import { forkJoin } from 'rxjs';
import { GenderResponse } from 'src/app/models/gender';
import { CategoryMasterResponse, CategorySubResponse } from 'src/app/models/category';
import { GarmentTypeResponse } from 'src/app/models/garment-type';
import { ColorResponse } from 'src/app/models/color';
import { SeasonResponse } from 'src/app/models/season';
import { UsageResponse } from 'src/app/models/usage';
import { UtilsService } from 'src/app/servises/utils.service';

@Component({
  selector: 'app-garment-editor',
  standalone: false,
  templateUrl: './garment-editor.component.html',
  styleUrl: './garment-editor.component.scss',
})
export class GarmentEditorComponent implements OnInit {
  garmentForm: FormGroup;

  isEditMode = false;
  garmentId: number | null = null;
  isLoading = false;
  isUploadingImage = false;

  displayedImageLinkOrB64: string | null = null;  // currently shown
  originalImage1: string | null = null;  // From DB (image_link)
  newImagePreviewB64: string | null = null; // Preview of new file
  newImageFile: File | null = null;     // New file selected

  errorMessage: string = '';
  successMessage: string = '';

  genders: GenderResponse[] = [];
  masterCategories: CategoryMasterResponse[] = [];
  subCategories: CategorySubResponse[] = [];
  garmentTypes: GarmentTypeResponse[] = [];
  colors: ColorResponse[] = [];
  seasons: SeasonResponse[] = [];
  usages: UsageResponse[] = [];

  constructor(
    private fb: FormBuilder,
    private garmentService: GarmentService,
    private imageService: ImageService,
    private genderService: GenderService,
    private categoryService: CategoryService,
    private garmentTypeService: GarmentTypeService,
    private colorService: ColorService,
    private seasonService: SeasonService,
    private usageService: UsageService,
    private utilsService: UtilsService,
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar,
  ) {
    this.garmentForm = this.fb.group({
      name: [null],
      description: [null],
      hex: [null, [
        /*this.HexValidator()*/
      ]],
      // image_link: [''], //todo add?
      last_worn: [null],
      gender_id: [null],
      category_master_id: [null],
      category_sub_id: [null],
      garment_type_id: [null],
      color_id: [null],
      season_id: [null],
      usage_id: [null]
    });
  }

  ngOnInit(): void {
    this.loadDropdownData();
    this.route.params.subscribe(params => {
      if (params['id']) {
        this.isEditMode = true;
        this.garmentId = Number(params['id']);
        this.loadGarment(this.garmentId);
      }
    });
  }

  loadDropdownData(): void {
    forkJoin({
      genders: this.genderService.getAllGendersObservable(),
      masterCategories: this.categoryService.getAllMasterCategoriesObservable(),
      subCategories: this.categoryService.getAllSubCategoriesObservable(),
      garmentTypes: this.garmentTypeService.getAllGarmentTypesObservable(),
      colors: this.colorService.getAllColorsObservable(),
      seasons: this.seasonService.getAllSeasonsObservable(),
      usages: this.usageService.getAllUsesObservable()
    }).subscribe({
      next: (data) => {
        this.genders = data.genders;
        this.masterCategories = data.masterCategories;
        this.subCategories = data.subCategories;
        this.garmentTypes = data.garmentTypes;
        this.colors = data.colors;
        this.seasons = data.seasons;
        this.usages = data.usages;
      },
      error: (error) => {
        console.error('Error loading dropdown data:', error);
        this.snackBar.open('Error loading form options', 'Close');
      }
    });
  }

  loadGarment(id: number): void {
    this.isLoading = true;
    this.garmentService.getGarmentByIdObservable(id).subscribe({
      next: (garment) => {
        // console.log(garment);
        this.garmentForm.patchValue(garment);

        if (garment.image_link) {
          this.originalImage1 = garment.image_link;
          this.displayedImageLinkOrB64 = this.imageService.getImageLink(this.originalImage1);
        }

        this.isLoading = false;
      },
      error: (error) => {
        // console.error(error);
        this.errorMessage = error.error?.detail || 'An error occurred loading garment.';
        this.snackBar.open(this.errorMessage, 'Close');
        this.isLoading = false;
      }
    })


  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      if (!file.type.startsWith('image/')) {
        this.snackBar.open('Please select an image file', 'Close');
        return;
      }

      const maxSizeBytes = 10 * 1024 * 1024;
      if (file.size > maxSizeBytes) {
        this.snackBar.open('File size must be less than 10MB', 'Close');
        return;
      }

      this.newImageFile = file;

      const reader = new FileReader();
      reader.onload = (e: ProgressEvent<FileReader>) => {
        this.newImagePreviewB64 = e.target?.result as string;
        this.displayedImageLinkOrB64 = this.newImagePreviewB64;

      };
      reader.readAsDataURL(file);
    }
  }

  removeNewImage(): void {
    this.newImageFile = null;
    this.newImagePreviewB64 = null;

    if (this.originalImage1) {
      this.displayedImageLinkOrB64 = this.imageService.getImageLink(this.originalImage1);
    } else {
      this.displayedImageLinkOrB64 = null;
    }

    const fileInput = document.getElementById('imageInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  triggerFileInput(): void {
    const fileInput = document.getElementById('imageInput') as HTMLInputElement;
    fileInput?.click();
  }

  onSubmit(): void {
    if (this.garmentForm.invalid) {
      this.garmentForm.markAllAsTouched();
      return;
    }
    if (this.newImageFile) {
      this.uploadAndSaveGarment();
    } else {
      this.saveGarment();
    }
  }

  uploadAndSaveGarment(): void {
    if (!this.newImageFile) return;

    this.isUploadingImage = true;

    this.imageService.uploadImage(this.newImageFile).subscribe({
      next: (response) => {
        if (this.originalImage1) {

          this.imageService.deleteImage(this.originalImage1).subscribe({
            // next: () => console.log('Old image deleted'), 
            error: (error) => {
              this.errorMessage = error.error?.detail || 'Error deleting old image.';
              // this.errorMessage = 'Error deleting old image.';
              this.snackBar.open(this.errorMessage, 'Close');
            }
          });

        }
        // Save garment with new image filename
        this.saveGarment(response.filename_store);

        this.isUploadingImage = false;
      },
      error: (error) => {
        console.error(error);
        // this.errorMessage = error.error?.detail || 'An error occurred during uploading image.';
        this.errorMessage = 'An error occurred during uploading image.';
        this.snackBar.open(this.errorMessage, 'Close');
        this.isUploadingImage = false;
      }
    });
  }

  saveGarment(imageFilename?: string): void {
    this.isLoading = true;
    let garmentData: GarmentCreate = this.garmentForm.value;

    if (imageFilename) {
      garmentData.image_link = imageFilename;
    } else if (this.originalImage1) {
      garmentData.image_link = this.originalImage1;
    }

    garmentData = this.utilsService.removeEmptyFields(garmentData);

    if (this.isEditMode && this.garmentId) {
      this.updateGarment(this.garmentId, garmentData);
    } else {
      this.createGarment(garmentData);
    }
  }

  createGarment(garment: GarmentCreate): void {
    this.garmentService.createGarmentObservable(garment).subscribe({
      next: (response) => {
        // console.log(response)
        this.snackBar.open('Garment created successfully', 'Close', { duration: 5000 })
        this.router.navigate(['/clothes']);
        this.isLoading = false;
      },
      error: (error) => {
        // console.error(error);
        this.errorMessage = error.error?.detail || 'An error occurred during create.';
        this.snackBar.open(this.errorMessage, 'Close');
        this.isLoading = false;
      }
    });
  }

  updateGarment(id: number, garment: GarmentCreate): void {
    this.garmentService.updateGarmentObservable(id, garment).subscribe({
      next: (response) => {
        this.snackBar.open('Garment updated successfully', 'Close', { duration: 5000 });
        this.router.navigate(['/clothes']);
        this.isLoading = false;
      },
      error: (error) => {
        this.errorMessage = error.error?.detail || 'An error occurred during update.';
        this.snackBar.open(this.errorMessage, 'Close');
        this.isLoading = false;
      }
    });
  }

  onCancel(): void {
    this.router.navigate(['/clothes']);
  }

  onDelete(): void {
    if (!this.garmentId) return;

    this.isLoading = true;
    let garmentDeleteSecces = false;
    let imageDeleteSecces = false;

    this.garmentService.deleteGarmentObservable(this.garmentId).subscribe({
      next: (res) => {
        garmentDeleteSecces = true;

        if (this.originalImage1) {
          this.imageService.deleteImage(this.originalImage1).subscribe({
            next: (res) => {
              console.log(res);
              imageDeleteSecces = true;
            },
            error: (error) => {
              this.errorMessage = error.error?.detail || 'An error occurred during delete.';
              this.snackBar.open(this.errorMessage, 'Close');
              this.isLoading = false;
            }
          })
        } else {
          imageDeleteSecces = true;
        }

        if (garmentDeleteSecces && imageDeleteSecces) {
          this.snackBar.open('Garment deleted successfully', 'Close', { duration: 5000 });
        }

        this.isLoading = false;
        this.router.navigate(['/clothes']);

      },
      error: (error) => {
        this.errorMessage = error.error?.detail || 'An error occurred during delete.';
        this.snackBar.open(this.errorMessage, 'Close');

        this.isLoading = false;
      }
    })



  }

  classifyImage() {
    this.isUploadingImage = true;

    if (this.newImageFile) {
      this.executeClassificationWrapper(
        this.garmentService.classifyGarmentImageFileObservable(this.newImageFile)
      );
      return;
    }

    if (this.originalImage1) {
      const imageUrl = this.imageService.getImageLink(this.originalImage1);
      fetch(imageUrl)
        .then(res => {
          if (!res.ok) throw new Error('Network response was not ok');
          return res.blob();
        })
        .then(blob => {
          const file = new File([blob], this.originalImage1!, { type: blob.type });
          this.executeClassificationWrapper(
            this.garmentService.classifyGarmentImageFileObservable(file)
          );
        })
        .catch(err => {
          console.error('Error fetching existing image:', err);
          this.snackBar.open('Could not load server image for classification', 'Close');
          this.isUploadingImage = false;
        });
      return;
    }

    if (this.displayedImageLinkOrB64 && this.displayedImageLinkOrB64.startsWith('data:')) {
      this.executeClassificationWrapper(
        this.garmentService.classifyGarmentImageBase64Observable(this.displayedImageLinkOrB64)
      );
      return;
    }

    this.isUploadingImage = false;
    this.snackBar.open('No image to classify', 'Close');
  }


  private executeClassificationWrapper(observable$: any) {
    observable$.subscribe({
      next: (response: any) => {
        this.garmentForm.patchValue(response);
        this.isUploadingImage = false;
        this.snackBar.open('Classification complete!', 'Close', { duration: 5000 });
      },
      error: (error: any) => {
        console.error(error);
        this.errorMessage = 'An error occurred during image classification.';
        this.snackBar.open(this.errorMessage, 'Close');
        this.isUploadingImage = false;
      }
    });
  }
}
